import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  PartyDataForm,
  type PartyDataFormValues,
} from '../components/billing/PartyDataForm';
import { useHotel } from '../contexts/HotelContext';
import { useToast } from '../contexts/ToastContext';
import { getSupportEmail } from '../utils/domainConfig';
import {
  useBillingDataQuery,
  useSaveBillingDataMutation,
} from '../hooks/useSelfServiceBilling';
import { selfServiceBillingApi } from '../services/selfServiceBillingApi';

/**
 * TenantCompany Page (/tenant/company)
 *
 * Displays and allows editing of company/billing data via self-service endpoint.
 * Uses shared <PartyDataForm mode="edit-self-service">.
 *
 * AC-1: Fetches from GET /billing/self-service/billing-data
 * AC-2: Form with validation (NIP read-only after set)
 * AC-3: Save via POST /billing/self-service/billing-data
 * AC-4: Toast success/error messages
 * AC-5: Skeleton loading + error state with retry
 * AC-6: Rate limit 429 handling
 *
 * @see docs/specifications/2026-03-12-pa7-user-self-service-billing-ui.md
 */
const TenantCompany: React.FC = () => {
  const { currentTenant } = useHotel();
  const { addToast } = useToast();
  const [rateLimitHit, setRateLimitHit] = useState(false);

  const {
    data: billingData,
    isLoading,
    isError,
    refetch,
  } = useBillingDataQuery();

  const saveMutation = useSaveBillingDataMutation();

  const supportEmail =
    currentTenant?.brand?.replyToAddress || getSupportEmail();

  const nipAlreadySet = !!billingData?.nip;

  const handleSubmit = useCallback(
    async (values: PartyDataFormValues) => {
      try {
        setRateLimitHit(false);
        await saveMutation.mutateAsync({
          nip: values.nip,
          companyName: values.companyName || null,
          street: values.street || null,
          city: values.city || null,
          postalCode: values.postalCode || null,
          country: values.country,
          billingEmail: values.billingEmail || undefined,
        });

        addToast({
          type: 'success',
          message: 'Dane firmowe zostały zapisane.',
        });
      } catch (err: unknown) {
        const axiosError = err as {
          response?: { status?: number; data?: { message?: string } };
        };
        const status = axiosError.response?.status;

        if (status === 429) {
          setRateLimitHit(true);
          addToast({
            type: 'error',
            message: 'Przekroczono limit zmian. Spróbuj za godzinę.',
          });
        } else if (status === 422) {
          // GUS unavailable
          addToast({
            type: 'warning',
            message: 'Dane GUS niedostępne. Wypełnij dane ręcznie.',
          });
        } else {
          addToast({
            type: 'error',
            message: 'Nie udało się zapisać danych. Spróbuj ponownie.',
          });
        }
      }
    },
    [saveMutation, addToast],
  );

  const initialValues = useMemo(() => ({
    companyName: billingData?.companyName || '',
    nip: billingData?.nip || '',
    street: billingData?.street || '',
    city: billingData?.city || '',
    postalCode: billingData?.postalCode || '',
    country: billingData?.country || 'PL',
    billingEmail: billingData?.billingEmail || '',
  }), [billingData]);

  // Loading state (AC-5)
  if (isLoading) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto">
          <div data-testid="company-loading" aria-live="polite" aria-busy="true">
            {/* Breadcrumb skeleton */}
            <div className="mb-6">
              <div className="h-4 w-48 bg-gray-200 rounded animate-pulse" />
            </div>
            {/* Title skeleton */}
            <div className="h-8 w-64 bg-gray-200 rounded animate-pulse mb-6" />
            {/* Form skeleton */}
            <div className="bg-white rounded-lg shadow p-6 space-y-4">
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div key={i}>
                  <div className="h-4 w-24 bg-gray-200 rounded animate-pulse mb-2" />
                  <div className="h-10 w-full bg-gray-200 rounded animate-pulse" />
                </div>
              ))}
              <div className="h-10 w-24 bg-gray-200 rounded animate-pulse mt-4" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Error state (AC-5)
  if (isError) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Link
              to="/tenant/subscription"
              className="text-sm text-primary-700 hover:text-primary-800 hover:underline"
            >
              &larr; Wstecz do Subskrypcji
            </Link>
          </div>

          <div
            data-testid="company-error"
            className="bg-red-50 border border-red-200 rounded-lg p-6"
            role="alert"
          >
            <h2 className="text-xl font-semibold text-red-800 mb-2">
              Błąd wczytywania danych firmowych
            </h2>
            <p className="text-red-700">
              Nie udało się załadować danych firmowych. Spróbuj ponownie.
            </p>
            <button
              data-testid="company-retry-btn"
              onClick={() => refetch()}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Spróbuj ponownie
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        {/* Breadcrumb (AC-14) */}
        <div className="mb-6">
          <Link
            to="/tenant/subscription"
            className="text-sm text-primary-700 hover:text-primary-800 hover:underline"
          >
            &larr; Wstecz do Subskrypcji
          </Link>
        </div>

        <h1 className="text-3xl font-bold mb-6">Dane firmowe</h1>

        <div className="bg-white rounded-lg shadow p-6">
          <PartyDataForm
            mode="edit-self-service"
            initialValues={initialValues}
            onSubmit={handleSubmit}
            nipAlreadySet={nipAlreadySet}
            supportEmail={supportEmail}
            isSubmitting={saveMutation.isPending}
            rateLimitHit={rateLimitHit}
            onGusLookup={nipAlreadySet ? undefined : selfServiceBillingApi.gusLookup}
          />
        </div>
      </div>
    </Layout>
  );
};

export default TenantCompany;
